import React, { useState, useMemo } from 'react';
import { Trophy, Plus, Trash2, Medal, RefreshCw, Download } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Loader } from '../../components/ui/Loader';
import { Modal } from '../../components/ui/Modal';
import { pdfService } from '../../services';
import { useResults, useSchools, useEvents, useResultMutations, useLogMutations } from '../../hooks/useConvex';
import { School, Event } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { Id } from '../../../convex/_generated/dataModel';

interface EventResult {
    id?: string;
    event_name: string;
    school_code: string;
    school_name: string;
    position: number;
    points: number;
    created_at: string;
}

interface SchoolScore {
    school_code: string;
    school_name: string;
    total_points: number;
    event_wins: number;
}

const POINTS_SYSTEM = {
    1: 10,  // 1st place
    2: 7,   // 2nd place
    3: 5,   // 3rd place
};

export const ResultsPage: React.FC = () => {
    const { user, adminRole, assignedEvents } = useAuth();
    const resultsData = useResults();
    const schoolsData = useSchools();
    const eventsData = useEvents();
    const { create, remove } = useResultMutations();
    const { logAdminActivity } = useLogMutations();
    const loading = resultsData === undefined || schoolsData === undefined || eventsData === undefined;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [selectedPosition, setSelectedPosition] = useState<number>(1);

    const [formData, setFormData] = useState({
        event_name: '',
        school_code: '',
        position: 1,
    });

    // Transform Convex data
    const results = useMemo(() => {
        if (!resultsData) return [];
        return resultsData.map((r: Record<string, unknown>) => ({
            id: r._id as string,
            event_name: r.event_name as string,
            school_code: r.school_code as string,
            school_name: r.school_name as string,
            position: r.position as number,
            points: r.points as number,
            created_at: r._creationTime ? new Date(r._creationTime as number).toISOString() : new Date().toISOString(),
        })) as EventResult[];
    }, [resultsData]);

    const schools = useMemo(() => {
        if (!schoolsData) return [];
        const transformed = schoolsData.map((s: Record<string, unknown>) => ({
            ...s,
            school_id: s._id,
        })) as School[];

        // Sort schools by team code
        return transformed.sort((a: School, b: School) =>
            (a.team_code || '').localeCompare(b.team_code || '')
        );
    }, [schoolsData]);

    const events = useMemo(() => {
        if (!eventsData) return [];
        const transformed = eventsData.map((e: Record<string, unknown>) => ({
            ...e,
            event_id: e._id,
        })) as Event[];

        // Sort events by name
        return transformed.sort((a: Event, b: Event) =>
            (a.name || '').localeCompare(b.name || '')
        );
    }, [eventsData]);

    const isEventManager = adminRole === 'event_manager';
    const canManageEvent = (eventName: string) => {
        if (adminRole === 'supreme_admin' || adminRole === 'admin') return true;
        if (isEventManager) {
            // Find event ID by name since results store event_name
            const event = events.find(e => e.name === eventName);
            return event ? assignedEvents.includes(event.event_id) : false;
        }
        return false;
    };

    const leaderboard = useMemo(() => {
        const scores = new Map<string, SchoolScore>();

        results.forEach(result => {
            if (!scores.has(result.school_code)) {
                scores.set(result.school_code, {
                    school_code: result.school_code,
                    school_name: result.school_name,
                    total_points: 0,
                    event_wins: 0,
                });
            }

            const school = scores.get(result.school_code)!;
            school.total_points += result.points;
            if (result.position === 1) school.event_wins++;
        });

        return Array.from(scores.values())
            .sort((a, b) => b.total_points - a.total_points);
    }, [results]);

    const handleAddResult = async () => {
        if (!formData.event_name || !formData.school_code) {
            alert('Please fill in all fields');
            return;
        }

        if (!canManageEvent(formData.event_name)) {
            alert('You do not have permission to add results for this event');
            return;
        }

        try {
            setActionLoading(true);
            const points = POINTS_SYSTEM[selectedPosition as keyof typeof POINTS_SYSTEM] || 0;
            const selectedSchool = schools.find(s => s.team_code === formData.school_code);

            await create({
                eventName: formData.event_name,
                schoolCode: formData.school_code,
                schoolName: selectedSchool?.school_name || formData.school_code,
                position: selectedPosition,
                points: points,
            });

            // Log activity
            await logAdminActivity({
                email: user?.email || '',
                action: 'add_result',
                details: `Added result: ${formData.event_name} - ${formData.school_code} (Position ${selectedPosition})`,
            });

            setIsModalOpen(false);
            setFormData({ event_name: '', school_code: '', position: 1 });
            setSelectedPosition(1);
        } catch (error: any) {
            alert(error.message || 'Failed to add result');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteResult = async (resultId: string, eventName: string) => {
        if (!canManageEvent(eventName)) {
            alert('You do not have permission to delete results for this event');
            return;
        }

        if (!window.confirm(`Are you sure you want to delete this result for "${eventName}"? This action cannot be undone.`)) {
            return;
        }

        setDeletingId(resultId);
        try {
            const result = results.find(r => r.id === resultId);
            await remove({ resultId: resultId as Id<'event_results'> });

            // Log activity
            await logAdminActivity({
                email: user?.email || '',
                action: 'delete_result',
                details: `Deleted result: ${eventName} - ${result?.school_code}`,
            });
        } catch (error: any) {
            console.error('Error deleting result:', error);
            alert(error.message || 'Failed to delete result');
        } finally {
            setDeletingId(null);
        }
    };

    const getPositionBadgeColor = (position: number): 'warning' | 'neutral' | 'info' => {
        const colors: Record<number, 'warning' | 'neutral' | 'info'> = {
            1: 'warning', // Gold
            2: 'neutral', // Silver
            3: 'info',    // Bronze
        };
        return colors[position] || 'neutral';
    };

    const handleExportPDF = () => {
        const data = results.map(result => [
            result.event_name,
            (() => {
                const school = schools.find(s => s.team_code === result.school_code);
                return result.school_name + (school?.branch_name ? ` (${school.branch_name})` : '');
            })(),
            result.school_code,
            result.position === 1 ? '1st' : result.position === 2 ? '2nd' : result.position === 3 ? '3rd' : `${result.position}th`,
            result.points.toString(),
            new Date(result.created_at).toLocaleDateString()
        ]);

        pdfService.generatePDF({
            title: 'Event Results',
            filename: 'event_results',
            columns: ['Event', 'School', 'Code', 'Position', 'Points', 'Date'],
            data: data,
            orientation: 'landscape',
        });
    };

    // Filter events for dropdown based on permissions
    const availableEvents = isEventManager
        ? events.filter(e => assignedEvents.includes(e.event_id))
        : events;

    return (
        <div className="space-y-8">
            {/* Page Header */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-brand/[0.08] border border-brand/[0.12] flex items-center justify-center">
                            <Trophy className="w-5 h-5 text-brand" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-display font-bold text-white tracking-tight">Results & Leaderboard</h1>
                            <p className="text-sm text-white/30 mt-0.5">Track competition outcomes and school rankings</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.location.reload()}
                            isLoading={loading}
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleExportPDF}>
                            <Download className="w-4 h-4" />
                        </Button>
                        {(adminRole === 'supreme_admin' || adminRole === 'admin' || isEventManager) && (
                            <Button variant="primary" onClick={() => setIsModalOpen(true)}>
                                <Plus className="w-4 h-4 mr-2" />
                                Add Result
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Leaderboard Section */}
            <Card gradientBorder className="p-6">
                <div className="mb-6">
                    <div className="flex items-center gap-3 mb-2">
                        <Medal className="w-5 h-5 text-brand" />
                        <h2 className="text-lg font-display font-bold text-white tracking-tight">School Rankings</h2>
                    </div>
                    <p className="text-sm text-white/30">Top performing schools across all events</p>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <Loader size="lg" />
                    </div>
                ) : leaderboard.length === 0 ? (
                    <div className="text-center py-12">
                        <Trophy className="w-12 h-12 mx-auto mb-4 text-white/10" />
                        <p className="text-lg font-medium text-white/40">No results recorded yet</p>
                        <p className="text-sm text-white/20 mt-1">Add event results to see the leaderboard</p>
                    </div>
                ) : (
                    <div className="space-y-0">
                        {leaderboard.map((school, index) => (
                            <div
                                key={school.school_code}
                                className="flex items-center gap-4 py-4 border-b border-white/[0.03] last:border-0"
                            >
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                    index === 0
                                        ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-black shadow-[0_0_16px_rgba(251,191,36,0.3)]'
                                        : index === 1
                                        ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-black'
                                        : index === 2
                                        ? 'bg-gradient-to-br from-orange-700 to-orange-900 text-orange-200'
                                        : 'bg-white/[0.04] text-white/40'
                                }`}>
                                    {index + 1}
                                </div>
                                <div className="flex-1">
                                    <p className="text-base font-semibold text-white">{school.school_name}</p>
                                    <div className="flex items-center gap-3 text-xs text-white/30 mt-0.5">
                                        <span className="font-mono">{school.school_code}</span>
                                        <span className="flex items-center gap-1">
                                            <Trophy className="w-3 h-3" /> {school.event_wins} wins
                                        </span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-2xl font-mono font-bold text-brand tracking-tight">
                                        {school.total_points}
                                    </p>
                                    <p className="text-[10px] uppercase tracking-wider text-white/25">points</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Results Table */}
            <Card className="overflow-hidden">
                <div className="px-6 py-4 border-b border-white/[0.06] flex justify-between items-center">
                    <h2 className="text-sm font-display font-semibold text-white">Event Results</h2>
                    <Badge variant="neutral" size="sm">{results.length} Total</Badge>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-white/[0.02]">
                            <tr>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Event</th>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">School</th>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Position</th>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Points</th>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Date</th>
                                <th className="px-6 py-3.5 text-right text-[11px] font-medium uppercase tracking-wider text-white/25">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.03]">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center">
                                        <Loader size="md" className="mx-auto" />
                                    </td>
                                </tr>
                            ) : results.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-16 text-center text-white/30">
                                        No results found
                                    </td>
                                </tr>
                            ) : (
                                results.map((result) => (
                                    <tr key={result.id} className="hover:bg-white/[0.02] transition-colors duration-150">
                                        <td className="px-6 py-4 text-white font-medium">{result.event_name}</td>
                                        <td className="px-6 py-4">
                                            <span className="text-white/70">{result.school_name}</span>
                                            <span className="ml-2 text-xs text-white/25 font-mono">({result.school_code})</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge variant={getPositionBadgeColor(result.position)} size="sm">
                                                <span className="flex items-center gap-1.5">
                                                    <Medal className="w-3.5 h-3.5" />
                                                    {result.position === 1 ? '1st' : result.position === 2 ? '2nd' : result.position === 3 ? '3rd' : `${result.position}th`}
                                                </span>
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-brand font-mono font-bold text-lg">
                                                {result.points}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-white/25 font-mono">
                                            {new Date(result.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            {canManageEvent(result.event_name) && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => result.id && handleDeleteResult(result.id, result.event_name)}
                                                    className="text-white/30 hover:text-danger hover:bg-danger/10"
                                                    isLoading={deletingId === result.id}
                                                    disabled={!!deletingId}
                                                >
                                                    {deletingId !== result.id && <Trash2 className="w-4 h-4" />}
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            {/* Add Result Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Add Event Result"
                size="md"
            >
                <div className="space-y-5">
                    <div>
                        <label className="block text-sm font-medium text-white/60 mb-2">
                            Event
                        </label>
                        <select
                            value={formData.event_name}
                            onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
                            className="w-full px-4 py-3 rounded-lg bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] text-white focus:border-brand/50 focus:outline-none appearance-none"
                        >
                            <option value="" className="bg-[#0A0A0A]">Select Event</option>
                            {availableEvents.map((event) => (
                                <option key={event.event_id} value={event.name} className="bg-[#0A0A0A]">
                                    {event.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-white/60 mb-2">
                            School
                        </label>
                        <select
                            value={formData.school_code}
                            onChange={(e) => setFormData({ ...formData, school_code: e.target.value })}
                            className="w-full px-4 py-3 rounded-lg bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] text-white focus:border-brand/50 focus:outline-none appearance-none"
                        >
                            <option value="" className="bg-[#0A0A0A]">Select School</option>
                            {schools.filter(s => s.is_active).map((school) => (
                                <option key={school.school_id} value={school.team_code} className="bg-[#0A0A0A]">
                                    {school.team_code} - {school.school_name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-white/60 mb-3">
                            Position
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                            {[
                                { position: 1, label: '1st Place', points: 10, icon: '🥇', color: 'from-amber-400 to-amber-600' },
                                { position: 2, label: '2nd Place', points: 7, icon: '🥈', color: 'from-gray-300 to-gray-500' },
                                { position: 3, label: '3rd Place', points: 5, icon: '🥉', color: 'from-orange-700 to-orange-900' },
                            ].map((item) => (
                                <div
                                    key={item.position}
                                    onClick={() => setSelectedPosition(item.position)}
                                    className={`cursor-pointer p-4 rounded-xl border transition-all ${
                                        selectedPosition === item.position
                                            ? 'border-brand bg-brand/[0.08] shadow-[0_0_16px_rgba(255,153,28,0.15)]'
                                            : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12]'
                                    }`}
                                >
                                    <div className="text-center">
                                        <div className={`w-10 h-10 mx-auto mb-2 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center text-lg`}>
                                            {item.icon}
                                        </div>
                                        <p className="text-sm font-semibold text-white mb-0.5">{item.label}</p>
                                        <p className="text-xs text-white/40 font-mono">{item.points} pts</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                        <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleAddResult}
                            isLoading={actionLoading}
                            disabled={!formData.event_name || !formData.school_code}
                        >
                            Add Result
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
